import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDateTimeISO } from 'shared/util'
import { SisetuMatchingFormData, SisetuMatchingResult } from 'interface/sisetu/match'
import { KojinMatchingFormData, KojinMatchingResult } from 'interface/kojin/match'
import { KojinResult } from 'interface/kojin'
import { SisetuResult } from 'interface/sisetu'
import { useMatchingRequest } from 'interface/matching'
import { matchingScoreToState, MatchingState, MatchingCondition, MatchingResultData } from '../types'

type MatchingStats = {
	match?: number
	multiHit?: number
	maybe?: number
	unable?: number
	notFound?: number
	wait?: number
	memo?: number
}

type MatchingFormDataWithUUID = {
	target: 'kojin' | 'sisetu'
	uuid: string
	self: boolean
	form: SisetuMatchingFormData | KojinMatchingFormData
}

type MatchingResultDataWithUUID = {
	target: 'kojin' | 'sisetu'
	uuid: string
	result: MatchingResultData
}

type MatchingMemoWithUUID = {
	uuid: string
	memo: string
}

export const useMatching = () => {
	const refBlocking = useRef<boolean>(false)

	const [condition, setCondition] = useState<MatchingCondition | null>(null)
	const [formList, setFormList] = useState<MatchingFormDataWithUUID[]>([])
	const [resultList, setResultList] = useState<MatchingResultDataWithUUID[]>([])
	const [memoList, setMemoList] = useState<MatchingMemoWithUUID[]>([])
	const [stats, setStats] = useState<MatchingStats | null>(null)
	const [completedIndex, setCompletedIndex] = useState<number>(0)

	const [autoMatchingMode, setAutoMatchingMode] = useState<'wait' | 'matching' | 'stop' | 'finish'>('wait')
	const [autoMatchingIndex, setAutoMatchingIndex] = useState<number>(-1)
	const [filteredState, setFilterdState] = useState<MatchingState | null>(null)
	const [page, setPage] = useState<number>(0)
	const [autoMatchingError, setAutoMatchingError] = useState<Error | undefined>()
	const [openDcfCode, setOpenDcfCode] = useState<string | null>(null)

	const doSearch = useCallback(
		(condition: MatchingCondition) => {
			const { target, searchWords, senmon } = condition

			const prefix = formatDateTimeISO(new Date())
			const requestList: MatchingFormDataWithUUID[] = []
			const memoList: MatchingMemoWithUUID[] = []
			const addRequests = (cols: string[]) => {
				const name = cols.length > 0 ? cols[0] : ''
				const uuid = `${prefix}_${requestList.length + 1}`
				if (target === 'kojin') {
					const affiliation = cols.length > 1 ? cols[1] : undefined
					requestList.push({ target, uuid, self: false, form: { name, affiliation, senmon }})
				} else {
					const todofuken = cols.length > 1 ? cols[1] : undefined
					requestList.push({ target, uuid, self: false, form: { name, todofuken }})
				}
				if (cols.length > 2) {
					memoList.push({ uuid, memo: cols.slice(2).join('\n') })
				}
			}

			const data = searchWords.split('\n').flatMap(line => {
				const trimed = line.trim()
				return (trimed === '') ? [] : trimed
			}).join('\n') + '\n'

			let currentRow: string[] = []
			let currentField = ''
			let inQuote = false
			for (let i = 0; i < data.length; i++) {
				const char = data[i]
				if (inQuote) {
					if (char === '"') {
						if (i + 1 < data.length && data[i + 1] === '"') {
							currentField += '"'
							i++
						} else {
							inQuote = false
						}
					} else {
						currentField += char
					}
				} else {
					if (char === '"') {
						inQuote = true
					} else if (char === '\t') {
						currentRow.push(currentField)
						currentField = ''
					} else if (char === '\n') {
						currentRow.push(currentField)
						addRequests(currentRow)
						currentField = ''
						currentRow = []
					} else {
						currentField += char
					}
				}
			}
			setCondition(condition)
			setStats(null)
			setAutoMatchingError(undefined)
			setResultList([])
			setFilterdState(null)
			setPage(1)
			setMemoList(memoList)
			setFormList(requestList)
			setCompletedIndex(0)

			if (requestList.length > 0) {
				setAutoMatchingIndex(0)
				setAutoMatchingMode('matching')
			} else {
				setAutoMatchingIndex(-1)
				setAutoMatchingMode('finish')
			}
		},
		[setCondition, setAutoMatchingError, setAutoMatchingIndex, setResultList, setStats, setFilterdState, setPage]
	)

	const uuidList = useMemo(
		() => formList.map((request, index) => ({ index, uuid: request.uuid })),
		[formList]
	)

	const setMatchingForm = useCallback(
		(uuid: string, form: KojinMatchingFormData | SisetuMatchingFormData) => {
			setFormList([
				...formList.map(exist => ({
					...exist,
					form: exist.uuid === uuid ? form : exist.form
				}))
			])
		},
		[formList, setFormList]
	)
	const getMatchingForm = useCallback(
		(uuid: string) => formList.find(result => result.uuid === uuid),
		[formList]
	)

	useEffect(
		() => {
			const newStats: MatchingStats = {}
			if (uuidList.length > resultList.length) {
				newStats.wait = uuidList.length - resultList.length
			}
			if (memoList.length > 0) {
				newStats.memo = memoList.length
			}
			const applyMathingStats = (r: MatchingResultData) => {
				if (r.state === "match") newStats.match = (newStats.match ?? 0) + 1
				else if (r.state === "multiHit") newStats.multiHit = (newStats.multiHit ?? 0) + 1
				else if (r.state === "maybe") newStats.maybe = (newStats.maybe ?? 0) + 1
				else if (r.state === "unable") newStats.unable = (newStats.unable ?? 0) + 1
				else if (r.state === "notFound") newStats.notFound = (newStats.notFound ?? 0) + 1
			}
			resultList.forEach(({ result }) => applyMathingStats(result))

			setStats(newStats)
		},
		[uuidList, resultList, memoList, setStats]
	)

	const setMatchingResult = useCallback(
		(target: 'kojin' | 'sisetu', uuid: string, result: MatchingResultData) => {
			setResultList(prev => {
				let found = false
				const exists = prev.map(
					exist => {
						if (!found && exist.uuid === uuid) {
							found = true
							return { ...exist, result }
						}
						return exist
					}
				)
				if (found) {
					return [...exists]
				} else {
					return [...exists, { target, uuid, result }]
				}
			})
		},
		[setResultList]
	)
	const getMatchingResult = useCallback(
		(uuid: string) => resultList.find(result => result.uuid === uuid),
		[resultList]
	)

	const onMatched = useCallback(
		(target: 'kojin' | 'sisetu', uuid: string, result: KojinMatchingResult | SisetuMatchingResult) => {
			const firstChoice = result.list.length > 0 ? result.list[0] : null
			const isMultiHit = result.list.length > 1 ? true : false
			if (target === 'kojin') {
				const firstChoiceKojin = firstChoice as KojinResult | null
				setMatchingResult(target, uuid, {
					dcfCode: firstChoiceKojin?.kojinCode ?? null,
					kojin: firstChoiceKojin ?? undefined,
					candidates: result.list,
					isMultiHit,
					state: matchingScoreToState(firstChoiceKojin?.nameMatchScore, isMultiHit)
				})
			} else {
				const firstChoiceSisetu = firstChoice as SisetuResult | null
				setMatchingResult(target, uuid, {
					dcfCode: firstChoiceSisetu?.sisetuCode ?? null,
					sisetu: firstChoiceSisetu ?? undefined,
					candidates: result.list,
					isMultiHit,
					state: matchingScoreToState(firstChoiceSisetu ? 100 : null, isMultiHit)
				})
			}
		},
		[setMatchingResult]
	)

	type AutoMatchingParams = {
		target: 'kojin' | 'sisetu'
		uuid: string
		autoMatchingIndex: number
	}
	const onAutoMatched = useCallback(
		(result: KojinMatchingResult | SisetuMatchingResult, params: AutoMatchingParams) => {
			refBlocking.current = false
			onMatched(params.target, params.uuid, result)

			const next = params.autoMatchingIndex + 1
			if (next < uuidList.length) {
				setAutoMatchingIndex(next)
			} else {
				setAutoMatchingIndex(-1)
				setAutoMatchingMode('finish')
			}
		},
		[uuidList, setAutoMatchingIndex, setAutoMatchingMode, onMatched]
	)
	const onAutoMatchingError = useCallback(
		(reason: Error | undefined) => {
			setAutoMatchingError(reason)
			setAutoMatchingMode('stop')
			refBlocking.current = false
		},
		[setAutoMatchingError]
	)
	const [autoMatchingState, matching] = useMatchingRequest<AutoMatchingParams>({
		onPosted: onAutoMatched,
		onError: onAutoMatchingError
	})
	const autoMatchingStop = useCallback(
		() => {
			setAutoMatchingMode('stop')
		},
		[setAutoMatchingMode]
	)
	const autoMatchingResume = useCallback(
		() => {
			setAutoMatchingError(undefined)
			setAutoMatchingMode('matching')
		},
		[setAutoMatchingError, setAutoMatchingMode]
	)

	useEffect(
		() => {
			if (refBlocking.current === true) return
			if (autoMatchingIndex >= uuidList.length || autoMatchingIndex < 0) return
			if (autoMatchingMode !== 'matching') return

			const uuid = uuidList[autoMatchingIndex].uuid
			const form = getMatchingForm(uuid)
			if (!form || form.self) {
				 
				setAutoMatchingMode('stop')
				return
			}
			refBlocking.current = true
			matching({
				target: form.target,
				form: form.form,
				params: {
					target: form.target,
					uuid,
					autoMatchingIndex
				}
			})
		},
		[uuidList, autoMatchingMode, autoMatchingIndex, getMatchingForm, setAutoMatchingMode, matching]
	)

	const setMemo = useCallback(
		(uuid: string, memo: string) => {
			const trimed = memo.trim()
			setMemoList(prev => {
				if (trimed) {
					const it = prev.find(exist => exist.uuid === uuid)
					if (it) {
						// update it
						it.memo = trimed
						return [...prev]
					} else {
						// add it
						return [...prev, { uuid, memo }]
					}
				} else {
					// remove it
					return [...prev.filter(exist => exist.uuid !== uuid)]
				}
			})
		},
		[setMemoList]
	)
	const getMemo = useCallback(
		(uuid: string) => memoList.find(result => result.uuid === uuid)?.memo,
		[memoList]
	)

	const filteredList = useMemo(
		() => (
			filteredState === null ? uuidList :
			filteredState === "memo" ? uuidList.filter(({ uuid }) => memoList.find(r => r.uuid === uuid)?.memo ?? '' !== '') :
			uuidList.filter(({ uuid }) => resultList.find(r => r.uuid === uuid)?.result.state === filteredState)
		),
		[uuidList, resultList, memoList, filteredState]
	)

	const pageCount = useMemo(
		() => {
			return Math.ceil((filteredList.length + 1) / 100)
		},
		[filteredList]
	)

	const setFilterdStateAndResetPage = useCallback(
		(state: MatchingState | null) => {
			setFilterdState(state)
			setPage(1)
		},
		[setPage, setFilterdState]
	)

	const getResultCsv = useCallback(
		(target?: 'kojin' | 'sisetu', columns?: string[]) => uuidList.map(({ uuid }) => {
			const result = resultList.find(r => r.uuid === uuid)
			if (!result) return ''
			const lines = [result.result.dcfCode ?? '']
			if (columns) {
				if (target === 'kojin' && result.result.kojin) {
					const kojin = result.result.kojin
					columns.forEach(column => {
						lines.push(
							column === 'isiName' ? (kojin.isiName ?? '') :
							column === 'reason' ? (kojin.nameMatchReason ?? '') :
							''
						)
					})
				} else if (target === 'sisetu' && result.result.sisetu) {
					const sisetu = result.result.sisetu
					columns.forEach(column => {
						lines.push(
							column === 'sisetuName' ? (sisetu.sisetuName ?? '') :
							column === 'todofuken' ? (sisetu.juusyoKenName ?? '') :
							''
						)
					})
				} else {
					columns.forEach(_ => lines.push(''))
				}
			}
			if (memoList.length > 0) {
				lines.push(memoList.find(r => r.uuid === uuid)?.memo ?? '')
			}
			return lines.join('\t')
		}).join('\n'),
		[uuidList, resultList, memoList]
	)

	return {
		doSearch, condition, matchingStats: stats, uuidList, filteredList,
		setMatchingForm, getMatchingForm,
		setMatchingResult, getMatchingResult, onMatched,
		setMemo, getMemo,
		autoMatchingMode, autoMatchingIndex, autoMatchingState,
		autoMatchingError, autoMatchingResume, autoMatchingStop,
		filteredState, setFilterdState: setFilterdStateAndResetPage,
		pageCount, page, setPage,
		getResultCsv,
		openDcfCode, setOpenDcfCode,
		completedIndex, setCompletedIndex
	}
}